// Import react elements
import React, { useState } from "react";

// Import components
import RenderImageUploader from "global/components/ImageUploader"
import CreateButton from "global/components/CreateButton";

// Import helper
import { ResolveTemplatePreviewPayload } from "../utilities/ResolveTemplatePreviewPayload";

// Import css
import "./TemplatePreviewImagePicker.css";
import { CloseIcon } from "global/strich_icons/StrichIcons";



// This is the preset list for generic preview shapes
const GENERIC_PRESET_OPTIONS = [
    
    // This is the default branded logo option
    {
        id: "logo",
        label: "Logo",
        shape: "logo",
    },

    // This is the square option
    {
        id: "square",
        label: "Square",
        shape: "square",
    },

    // This is the circle option
    {
        id: "circle",
        label: "Circle",
        shape: "circle",
    },

    // This is the ISO portrait option
    {
        id: "iso-portrait",
        label: "ISO Portrait",
        shape: "isoPortrait",
    },

    // This is the ISO landscape option
    {
        id: "iso-landscape",
        label: "ISO Landscape",
        shape: "isoLandscape",
    },

    // This is the tall skinny rectangle option
    {
        id: "tall-rectangle",
        label: "Tall Rectangle",
        shape: "tallRectangle",
    },

    // This is the wide short rectangle option
    {
        id: "wide-rectangle",
        label: "Wide Rectangle",
        shape: "wideRectangle",
    },

    // This is the triangle option
    {
        id: "triangle",
        label: "Triangle",
        shape: "triangle",
    },

    // This is the explicit blank preview option
    {
        id: "none",
        label: "No Image",
        shape: "none",
    },

];



// This is the default colour list for generic shapes
const GENERIC_SHAPE_COLOURS = [
    
    // This is dark slate
    "#1F2937",

    // This is blue
    "#2563EB",

    // This is violet
    "#7C3AED",

    // This is pink
    "#DB2777",

    // This is orange
    "#EA580C",

    // This is green
    "#059669",

    // This is red
    "#DC2626",

    // This is black
    "#000000",

];



// Export component
export default function TemplatePreviewImagePicker({

    // This is the current preview selection
    value,

    // This is called when the preview selection changes
    onChange,

    // This is your software logo url
    logoUrl,

    // This is an optional button title override
    buttonTitle = "Choose Preview Image",

    // The current image assigned to this role
    currentImage

}) {


    // This controls whether the popup is open
    const [isOpen, setIsOpen] = useState(false);


    // This safely derives the selected title for the button
    //const selectedDisplayName = value?.displayName || "Software Logo";


    // This opens the popup
    const handleOpen = () => {

        // This opens the popup
        setIsOpen(true);

    };


    // This closes the popup
    const handleClose = () => {

        // This closes the popup
        setIsOpen(false);

    };


    // This applies a new value to the parent
    const handleApplyValue = (nextValue) => {

        // This updates the parent state
        onChange?.(nextValue);

        // This closes the popup
        setIsOpen(false);

    };



    // Return component
    return (

        <div className="templatePreviewImagePicker">

            <div className="templatePreviewImagePicker__header">

                <div className="templatePreviewImagePicker__image-container">

                    {currentImage && 

                        <img src={currentImage} alt="selectedDisplayName" className="templatePreviewImagePicker__image" /> 
                        
                    }

                </div>

                <CreateButton
                    title={buttonTitle}
                    size="s"
                    plain={false}
                    fullWidth={true}
                    type="button"
                    onClick={handleOpen} 
                    color="var(--surface-color)"    
                    textColor="var(--text-color)"
                    border="solid 1px var(--input-border)"
                />

                <div className="templatePreviewImagePicker__notice">

                    <strong>Preview only:</strong> Used to illustrate how designs may appear on products.

                </div>

            </div>

            {isOpen ? (

                <TemplatePreviewImagePickerPopup
                    value={value}
                    onApply={handleApplyValue}
                    onClose={handleClose}
                    logoUrl={logoUrl}
                />

            ) : null}

        </div>
    );

}



// This is the popup component
function TemplatePreviewImagePickerPopup({

    // This is the current value from parent
    value,

    // This is the apply callback
    onApply,

    // This is the close callback
    onClose,

    // This is the logo image url
    logoUrl,

}) {



    // This stores the local draft before applying
    const [draftValue, setDraftValue] = useState(() => value || {
        
        // This sets default source type
        sourceType: "generic",
        
        // This sets default preset
        presetId: "logo",
        
        // This sets default shape
        shape: "logo",
        
        // This sets default label
        displayName: "Software Logo",
        
        // This sets default fill colour
        fillColour: "#2563EB",
        
        // This sets default upload payload
        uploadedImage: null,

    });


    /*
    // This checks whether the current draft is a generic non-logo shape
    const showColourPicker = useMemo(() => {

        // This reads the source type safely
        const isGeneric = draftValue?.sourceType === "generic";

        // This reads the shape safely
        const shape = draftValue?.shape;

        // This returns true only for colourable shapes
        return isGeneric && shape !== "logo" && shape !== "none";

    }, [draftValue]);
    */


    // This handles clicking a preset tile
    const handleSelectPreset = (preset) => {

        // This handles the no image option
        if (preset.shape === "none") {

            // This updates the draft to blank mode
            setDraftValue((prev) => ({
                ...prev,
                sourceType: "none",
                presetId: "none",
                shape: "none",
                displayName: "No Image",
                uploadedImage: null,
            }));

            // This stops here
            return;

        }

        // This handles the logo option
        if (preset.shape === "logo") {

            // This updates the draft to logo mode
            setDraftValue((prev) => ({
                ...prev,
                sourceType: "generic",
                presetId: "logo",
                shape: "logo",
                displayName: "Software Logo",
                uploadedImage: null,
            }));

            // This stops here
            return;

        }

        // This updates the draft to generic shape mode
        setDraftValue((prev) => ({
            ...prev,
            sourceType: "generic",
            presetId: preset.id,
            shape: preset.shape,
            displayName: preset.label,
            uploadedImage: null,
        }));

    };



    // This handles changing the selected colour
    const handleSelectColour = (fillColour) => {

        // This updates the draft fill colour
        setDraftValue((prev) => ({
            ...prev,
            fillColour: fillColour,
        }));

    };



    // This handles upload change from your existing uploader
    const handleUploadChange = (payload) => {
       
        // If there's no image data return early
        if (!payload.imageBucketUrl) return;

        // This updates the draft to uploaded mode
        setDraftValue((prev) => ({
            ...prev,
            sourceType: "upload",
            presetId: "upload",
            shape: "upload",
            displayName: payload?.imageDetails?.name || "Uploaded Preview Image",
            uploadedImage: payload,
        }));
    
    };



    // This applies the selected preview image
    const handleApply = () => {
        
        // Resolve the selected option to an uploader-shaped payload
        const resolvedPayload = ResolveTemplatePreviewPayload({
            
            // This is the current draft config
            previewConfig: draftValue,
            
            // This is the software logo url
            logoUrl: logoUrl,
            
        });

        // Send the selected config back to parent if needed
        onApply?.({
            previewConfig: draftValue,
            resolvedPayload: resolvedPayload,
        });
        
    };



    // Return popup
    return (

         <div className="popup-overlay">

            <div className="popup-container popup-container--medium">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Choose a Preview Image

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            Use a generic shape, your logo, your own image, or no image at all to preview how template layouts may look across products.

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content popup-content__body">

                    <div className="templatePreviewImagePickerPopup__section">

                        <div className="templatePreviewImagePickerPopup__sectionTitle">
                            Shape Colour
                        </div>

                        <div className="templatePreviewImagePickerPopup__colourRow">

                            {GENERIC_SHAPE_COLOURS.map((colour) => (

                                <button
                                    key={colour}
                                    type="button"
                                    className={`templatePreviewImagePickerPopup__colourSwatch${draftValue?.fillColour === colour ? " templatePreviewImagePickerPopup__colourSwatch--selected" : ""}`}
                                    style={{
                                        backgroundColor: colour,
                                    }}
                                    onClick={() => handleSelectColour(colour)}
                                    aria-label={`Choose colour ${colour}`}
                                />

                            ))}

                        </div>
                        
                    </div>

                    <div className="templatePreviewImagePickerPopup__section">

                        <div className="templatePreviewImagePickerPopup__sectionTitle">
                            Images
                        </div>

                        <div className="templatePreviewImagePickerPopup__grid">

                            {/* Display the software logo first */}
                            {GENERIC_PRESET_OPTIONS
                                .filter((preset) => preset.shape === "logo")
                                .map((preset) => (
                                <PresetTile
                                    key={preset.id}
                                    preset={preset}
                                    isSelected={draftValue?.presetId === preset.id && draftValue?.sourceType !== "upload"}
                                    fillColour={draftValue?.fillColour}
                                    logoUrl={logoUrl}
                                    onClick={handleSelectPreset}
                                    draftValue={draftValue}
                                />
                            ))}

                            <div className={`templatePreviewImagePickerPopup__tile${draftValue?.sourceType === "upload" ? " templatePreviewImagePickerPopup__tile--selected" : ""}`}>

                                <div className="templatePreviewImagePickerPopup__tilePreview" style={{background: "none"}}>

                                    {draftValue?.sourceType === "upload" && draftValue?.uploadedImage?.imageBucketUrl ?
                                    
                                        <img src={draftValue?.uploadedImage?.imageBucketUrl} alt={draftValue?.displayName || "Uploaded Image"}/>
                                
                                        :
                                                
                                        <div style={{margin: "4px"}}>

                                            <RenderImageUploader
                                                label="Template Preview Image"
                                                selectedImage={draftValue?.sourceType === "upload" ? draftValue?.uploadedImage?.imageBucketUrl || draftValue?.image_url || null : null}
                                                onImageChange={handleUploadChange}
                                            />

                                        </div>

                                    }

                                </div>
                               
                                <label className="templatePreviewImagePickerPopup__tileLabel">Upload Your Own</label>

                            </div>

                            {/* Display the explicit no-image option last */}
                            {GENERIC_PRESET_OPTIONS
                                .filter((preset) => preset.shape === "none")
                                .map((preset) => (

                                    <PresetTile
                                        key={preset.id}
                                        preset={preset}
                                        isSelected={draftValue?.presetId === preset.id && draftValue?.sourceType !== "upload"}
                                        fillColour={draftValue?.fillColour}
                                        logoUrl={logoUrl}
                                        onClick={handleSelectPreset}
                                    />

                                ))}

                        </div>

                    </div>
                 
                    
                  
                    <div className="templatePreviewImagePickerPopup__section">

                        <div className="templatePreviewImagePickerPopup__sectionTitle">
                            Shapes
                        </div>

                        <div className="templatePreviewImagePickerPopup__grid">

                            {GENERIC_PRESET_OPTIONS
                                .filter((preset) => (
                                    preset.shape === "square" ||
                                    preset.shape === "circle" ||
                                    preset.shape === "triangle"
                                ))
                                .map((preset) => (

                                    <PresetTile
                                        key={preset.id}
                                        preset={preset}
                                        isSelected={draftValue?.presetId === preset.id && draftValue?.sourceType !== "upload"}
                                        fillColour={draftValue?.fillColour}
                                        logoUrl={logoUrl}
                                        onClick={handleSelectPreset}
                                    />

                                ))}

                        </div>

                    </div>

                    <div className="templatePreviewImagePickerPopup__section">

                        <div className="templatePreviewImagePickerPopup__sectionTitle">
                            Aspects
                        </div>

                        <div className="templatePreviewImagePickerPopup__grid">

                            {GENERIC_PRESET_OPTIONS
                                .filter((preset) => (
                                    preset.shape === "isoPortrait" ||
                                    preset.shape === "isoLandscape" ||
                                    preset.shape === "tallRectangle" ||
                                    preset.shape === "wideRectangle"
                                ))
                                .map((preset) => (

                                    <PresetTile
                                        key={preset.id}
                                        preset={preset}
                                        isSelected={draftValue?.presetId === preset.id && draftValue?.sourceType !== "upload"}
                                        fillColour={draftValue?.fillColour}
                                        logoUrl={logoUrl}
                                        onClick={handleSelectPreset}
                                    />

                                ))}

                        </div>

                    </div>

                </div>

                {/* Footer actions */}
                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center">

                    <div className="horizontal-align-content-center">

                        <div className="templatePreviewImagePickerPopup__previewBar">

                            <div className="templatePreviewImagePickerPopup__previewLabel">
                                Current Selection:
                            </div>

                            <div className="templatePreviewImagePickerPopup__previewValue">

                                {draftValue?.displayName || "Software Logo"}

                            </div>

                        </div>

                        <CreateButton type="button" title="Apply Preview Image" size="m" onClick={() => handleApply() } />

                        <CreateButton type="button" title="Cancel" isCancel={true} invert size="m" onClick={() => onClose() }/>

                    </div>
                    
                </div>

            </div>

        </div>
    );

}



// This is a reusable preset tile
function PresetTile({

    // This is the preset definition
    preset,

    // This tells us if the tile is selected
    isSelected,

    // This is the current fill colour
    fillColour,

    // This is the logo image url
    logoUrl,

    // This is the click handler
    onClick,

}) {

    // This builds the tile class safely
    const tileClassName = `templatePreviewImagePickerPopup__tile${isSelected ? " templatePreviewImagePickerPopup__tile--selected" : ""}`;

    // Return tile
    return (

        <div 
            className={tileClassName}
            onClick={() => onClick?.(preset)}
        >

            <div className="templatePreviewImagePickerPopup__tilePreview">

                {preset?.shape === "logo" ? (

                    <div className="templatePreviewImagePickerPopup__logoWrap">

                        <img
                            className="templatePreviewImagePickerPopup__logo"
                            src={logoUrl}
                            alt="Software Logo"
                        />

                    </div>

                ) : null}

                {preset?.shape === "square" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--square"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "circle" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--circle"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "isoLandscape" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--isoLandscape"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "isoPortrait" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--isoPortrait"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "tallRectangle" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--tallRectangle"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "wideRectangle" ? (

                    <div
                        className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--wideRectangle"
                        style={{
                            backgroundColor: fillColour,
                        }}
                    />

                ) : null}

                {preset?.shape === "triangle" ? (

                    <div className="templatePreviewImagePickerPopup__triangleWrap">

                        <div
                            className="templatePreviewImagePickerPopup__shape templatePreviewImagePickerPopup__shape--triangle"
                            style={{
                                borderBottomColor: fillColour,
                            }}
                        />

                    </div>

                ) : null}

                {preset?.shape === "none" ? (

                    <div className="templatePreviewImagePickerPopup__noneIconWrap">

                        <div className="templatePreviewImagePickerPopup__noneIconCircle" />

                        <div className="templatePreviewImagePickerPopup__noneIconSlash" />

                    </div>
                    
                ) : null}

            </div>

            <div className="templatePreviewImagePickerPopup__tileLabel">

                {preset?.label}

            </div>

        </div>

    );

}
